import { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  Container,
  Card,
  Badge,
  Spinner,
} from "react-bootstrap";
import { FaHistory } from "react-icons/fa";
import moment from "moment";
import axios from "axios";
import { BASE_URL, HEADERS } from "../../../utils/constant";

const UserViewModel = ({ show, handleClose, userId, user }) => {
  const [userLogs, setUserLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show || !userId) return;

    const fetchUserLogs = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${BASE_URL}/users/user-logs/${userId}/`,
          { headers: HEADERS },
        );

        setUserLogs(response.data || []);
      } catch (err) {
        console.error("Error fetching user logs:", err);
        setError("Failed to load user logs.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserLogs();
  }, [show, userId]);

  const hasFamily =
    user?.family_details && Object.values(user.family_details).some((v) => v);

  const hasBank =
    user?.bank_details && Object.values(user.bank_details).some((v) => v);

  const hasResident =
    user?.resident_details &&
    Object.values(user.resident_details).some((v) => v);

  const hasPermanent =
    user?.permanent_details &&
    Object.values(user.permanent_details).some((v) => v);

  const hasQualification = user?.qualifications?.some(
    (q) => q.qualification || q.board || q.year,
  );

  const hasExperience = user?.experiences?.some(
    (exp) => exp.company || exp.designation || exp.years,
  );

  const getStatusColor = (status) => {
    switch (status) {
      case "Present":
        return "#28a745";
      case "Absent":
        return "#dc3545";
      case "Half Day":
        return "#ffc107";
      default:
        return "#6c757d";
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      scrollable
      dialogClassName="custom-modern-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="w-100 d-flex justify-content-between align-items-center">
          <span className="fw-semibold">
            👤 {user?.first_name || ""} {user?.last_name || ""}
          </span>

          <span
            className="badge"
            style={{
              backgroundColor: getStatusColor(user?.today_attendance?.status),
              padding: "6px 12px",
              color: "#fff",
            }}
          >
            {user?.today_attendance?.status || "—"}
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading user data...</p>
          </div>
        ) : error ? (
          <p className="text-danger text-center">{error}</p>
        ) : (
          <Container fluid>
            <Card className="mb-3 shadow-sm">
              <Card.Body className="small">
                <h6 className="text-primary mb-3">Basic Information</h6>

                <Row>
                  <Col md={6}>
                    <p>
                      <strong>User ID:</strong> {user?.emp_id || "—"}
                    </p>
                    <p>
                      <strong>First Name:</strong> {user?.first_name || "—"}
                    </p>
                    <p>
                      <strong>Last Name:</strong> {user?.last_name || "—"}
                    </p>
                    <p>
                      <strong>Email:</strong> {user?.email || "—"}
                    </p>
                    <p>
                      <strong>Phone:</strong> {user?.phone_number || "—"}
                    </p>
                  </Col>

                  <Col md={6}>
                    <p>
                      <strong>Designation:</strong> {user?.designation || "—"}
                    </p>
                    <p>
                      <strong>Department:</strong> {user?.department || "—"}
                    </p>
                    <p>
                      <strong>Gender:</strong> {user?.gender || "—"}
                    </p>
                    <p>
                      <strong>Employment Type:</strong>{" "}
                      {user?.employment_type || "—"}
                    </p>
                    <p>
                      <strong>Date Joined:</strong>{" "}
                      {moment(user?.date_joined).format("DD-MM-YYYY")}
                    </p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            <Card className="mb-3 shadow-sm">
              <Card.Body className="small">
                <h6 className="text-primary mb-3">Attendance Settings</h6>

                <Row>
                  <Col md={6}>
                    <p>
                      <strong>Attendance Type:</strong>{" "}
                      {user?.attendence_type || "—"}
                    </p>
                  </Col>

                  <Col md={6}>
                    <p>
                      <strong>Punch Location:</strong>{" "}
                      {user?.is_location
                        ? "Within Company Premises"
                        : "Anywhere"}
                    </p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            {hasFamily && (
              <Card className="mb-3 shadow-sm">
                <Card.Body className="small">
                  <h6 className="text-primary mb-3">Family Details</h6>

                  <Row>
                    <Col md={6}>
                      <p>
                        <strong>Father:</strong>{" "}
                        {user?.family_details?.father_first_name}{" "}
                        {user?.family_details?.father_last_name}
                      </p>
                      <p>
                        <strong>Mother:</strong>{" "}
                        {user?.family_details?.mother_first_name}{" "}
                        {user?.family_details?.mother_last_name}
                      </p>
                    </Col>

                    <Col md={6}>
                      <p>
                        <strong>Marital Status:</strong>{" "}
                        {user?.family_details?.marital_status}
                      </p>
                      <p>
                        <strong>Spouse:</strong>{" "}
                        {user?.family_details?.spouse_first_name}{" "}
                        {user?.family_details?.spouse_last_name}
                      </p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
            {hasBank && (
              <Card className="mb-3 shadow-sm">
                <Card.Body className="small">
                  <h6 className="text-primary mb-3">Bank Details</h6>

                  <Row>
                    <Col md={6}>
                      <p>
                        <strong>Account No:</strong>{" "}
                        {user?.bank_details?.account_no}
                      </p>
                      <p>
                        <strong>Bank Name:</strong>{" "}
                        {user?.bank_details?.bank_name}
                      </p>
                    </Col>

                    <Col md={6}>
                      <p>
                        <strong>IFSC:</strong> {user?.bank_details?.ifsc}
                      </p>
                      <p>
                        <strong>Branch:</strong> {user?.bank_details?.branch}
                      </p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
            {(hasResident || hasPermanent) && (
              <Card className="mb-3 shadow-sm">
                <Card.Body className="small">
                  <h6 className="text-primary mb-3">Address Details</h6>

                  <Row>
                    {hasResident && (
                      <Col md={6}>
                        <h6 className="text-muted">Residential Address</h6>
                        <p>{user?.resident_details?.res_address}</p>
                        <p>
                          {user?.resident_details?.res_city},{" "}
                          {user?.resident_details?.res_state}
                        </p>
                      </Col>
                    )}

                    {hasPermanent && (
                      <Col md={6}>
                        <h6 className="text-muted">Permanent Address</h6>
                        <p>{user?.permanent_details?.perm_address}</p>
                        <p>
                          {user?.permanent_details?.perm_city},{" "}
                          {user?.permanent_details?.perm_state}
                        </p>
                      </Col>
                    )}
                  </Row>
                </Card.Body>
              </Card>
            )}
            {hasQualification && (
              <Card className="mb-3 shadow-sm">
                <Card.Body className="small">
                  <h6 className="text-primary mb-3">Qualifications</h6>

                  {user?.qualifications
                    ?.filter((q) => q.qualification || q.board || q.year)
                    .map((q, i) => (
                      <p key={i}>
                        {q.qualification && <strong>{q.qualification}</strong>}
                        {q.board && ` - ${q.board}`}
                        {q.year && ` (${q.year})`}
                      </p>
                    ))}
                </Card.Body>
              </Card>
            )}
            {hasExperience && (
              <Card className="mb-3 shadow-sm">
                <Card.Body className="small">
                  <h6 className="text-primary mb-3">Work Experience</h6>

                  {user?.experiences
                    ?.filter(
                      (exp) => exp.company || exp.designation || exp.years,
                    )
                    .map((exp, i) => (
                      <p key={i}>
                        {exp.company && <strong>{exp.company}</strong>}
                        {exp.designation && ` — ${exp.designation}`}
                        {exp.years && ` (${exp.years} years)`}
                      </p>
                    ))}
                </Card.Body>
              </Card>
            )}
            <Card className="shadow-sm">
              <Card.Body>
                <h5 className="text-primary mb-3">
                  <FaHistory className="me-2" />
                  User Activity Log
                </h5>

                {userLogs.length > 0 ? (
                  <div style={{ overflowX: "auto" }}>
                    <table className="inventory-table w-100">
                      <thead>
                        <tr>
                          <th style={{ minWidth: "150px" }}>User</th>
                          <th style={{ minWidth: "150px" }}>Module</th>
                          <th style={{ minWidth: "120px" }}>Action</th>
                          <th style={{ minWidth: "250px" }}>Description</th>
                          <th style={{ minWidth: "180px" }}>Date & Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userLogs.map((log) => (
                          <tr key={log.id}>
                            <td>{log.user?.full_name || "—"}</td>
                            <td>{log.module || "—"}</td>
                            <td>
                              <Badge bg="secondary">{log.action}</Badge>
                            </td>
                            <td>{log.description || "—"}</td>
                            <td>
                              {moment(log.created_at).format(
                                "DD-MM-YYYY hh:mm A",
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted text-center mb-0">
                    No user activity available.
                  </p>
                )}
              </Card.Body>
            </Card>
          </Container>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserViewModel;
